/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl, type MessageDescriptor } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Field } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives/compiled';
import SmartUserPicker, {
	type OptionData,
	type Team,
	type Value,
	isTeam,
} from '@atlaskit/smart-user-picker';
import { token } from '@atlaskit/tokens';

import type { FocusAskTeamData } from '../../types';

type Props = {
	name: string;
	label: MessageDescriptor;
	placeholder: MessageDescriptor;
	cloudId: string;
	orgId: string;
	value: FocusAskTeamData | null;
	onChange: (team: FocusAskTeamData | null) => void;
	isRequired?: boolean;
	fieldId?: string;
	isDisabled?: boolean;
	isClearable?: boolean;
	hideLabel?: boolean;
	isCompact?: boolean;
	width?: number | string;
	appearance?: 'default' | 'subtle';
};

function teamOptionToData(team: Team): FocusAskTeamData {
	const id = team.id;
	const name = team.name?.trim() ? team.name.trim() : id;
	const avatarUrl = team.avatarUrl;
	return avatarUrl ? { id, name, avatarUrl } : { id, name };
}

const styles = cssMap({
	compactSubtleControl: {
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

const COMPACT_CONTROL_HEIGHT = '2.5rem';

export const TeamPickerField = ({
	name,
	label,
	placeholder,
	cloudId,
	orgId,
	value,
	onChange,
	isRequired,
	fieldId,
	isDisabled = false,
	isClearable = true,
	hideLabel = false,
	isCompact = false,
	width = '100%',
	appearance,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const labelText = formatMessage(label);

	const handleChange = (selected: Value) => {
		if (!selected || Array.isArray(selected)) {
			onChange(null);
			return;
		}
		if (isTeam(selected)) {
			onChange(teamOptionToData(selected));
			return;
		}
		onChange(null);
	};

	const pickerKey = `${fieldId}-${value?.id ?? 'empty'}`;

	const smartUserPicker = (
		<SmartUserPicker
			key={pickerKey}
			fieldId={fieldId ?? ''}
			productKey="people"
			siteId={cloudId}
			orgId={orgId}
			width={width}
			height={isCompact ? COMPACT_CONTROL_HEIGHT : undefined}
			minHeight={isCompact ? COMPACT_CONTROL_HEIGHT : undefined}
			noBorder={isCompact}
			subtle={appearance === 'subtle'}
			includeGroups={false}
			includeUsers={false}
			includeTeams
			verifiedTeams={false}
			placeholder={formatMessage(placeholder)}
			placeholderAvatar="team"
			isClearable={isClearable}
			isDisabled={isDisabled}
			ariaLabel={hideLabel ? labelText : undefined}
			required={isRequired}
			prefetch
			defaultValue={value ? ({ id: value.id, type: 'team' } as OptionData) : undefined}
			onChange={handleChange}
			maxOptions={10}
		/>
	);

	const picker =
		isCompact && appearance === 'subtle' && !isDisabled ? (
			<Box xcss={styles.compactSubtleControl}>{smartUserPicker}</Box>
		) : (
			smartUserPicker
		);

	if (hideLabel) {
		return picker;
	}

	return (
		<Field name={name} label={labelText} isRequired={isRequired}>
			{() => picker}
		</Field>
	);
};
